<template>
  <v-card>
    <div class="performance-snapshot mb-2">
      <div class="d-flex align-center justify-space-between mb-4">
        <h2 class="text-secondary">{{ title }}</h2>
        <v-btn
          variant="text"
          density="comfortable"
          color="primary"
          @click="openGlossary"
        >
          What is this?
        </v-btn>
      </div>

      <!-- Performance Grid -->
      <v-table density="compact" class="performance-table" elevation="0">
        <caption v-if="tableCaption" class="v-assistive-text">
          {{
            tableCaption
          }}
        </caption>
        <thead>
          <tr>
            <th class="text-left" style="width: 180px"></th>
            <th
              v-for="gradeHeader in useDynamicData
                ? snapshotGrades.map((g) => g.shortcode)
                : mockGradeHeaders"
              :key="gradeHeader"
              class="text-center text-secondary font-weight-bold"
              style="position: relative"
            >
              <div class="th-wrapper">
                <span class="cell-content">
                  {{ gradeHeader }}
                </span>
                <div
                  v-if="currentGradeShortcode === gradeHeader"
                  class="active-grade-indicator"
                  :style="{ height: activeIndicatorHeight }"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(gatewayLabel, gwIndex) in useDynamicData
              ? snapshotGatewayLabels
              : mockGatewayRowLabels"
            :key="gatewayLabel"
          >
            <td
              class="text-left text-secondary font-weight-bold"
              :class="{ 'grade-specific': !gatewayLabel.includes('Gateway') }"
            >
              <router-link
                v-if="canCreateGatewayLink(gatewayLabel)"
                :to="getRouteIfValid(gatewayLabel)"
              >
                {{ gatewayLabel }}
              </router-link>
              <span v-else>{{ gatewayLabel }}</span>
            </td>
            <td
              v-for="(gradeHeader, gradeIndex) in useDynamicData
                ? snapshotGrades.map((g) => g.shortcode)
                : mockGradeHeaders"
              :key="`${gatewayLabel}-${gradeHeader}`"
              class="text-center py-4"
            >
              <StatusIcon
                v-if="
                  useDynamicData
                    ? gatewayStatusMatrix[gwIndex]?.[gradeIndex]
                    : mockStatusMatrix[gwIndex]?.[gradeIndex]
                "
                :status="
                  useDynamicData
                    ? gatewayStatusMatrix[gwIndex]?.[gradeIndex]
                    : mockStatusMatrix[gwIndex]?.[gradeIndex]
                "
                no-margin
              />
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- Legend -->
      <div class="d-flex align-center mt-6 legend">
        <div class="text-tonal-black font-weight-bold mr-2">Key:</div>
        <div class="d-flex align-center mr-3">
          <StatusIcon status="green" />
          <span class="text-tonal-black">Meets Expectations</span>
        </div>
        <div class="d-flex align-center mr-3">
          <StatusIcon status="yellow" />
          <span class="text-tonal-black">Partially Meets Expectations</span>
        </div>
        <div class="d-flex align-center mr-3">
          <StatusIcon status="red" />
          <span class="text-tonal-black">Does Not Meet Expectations</span>
        </div>
        <div class="d-flex align-center mr-3">
          <StatusIcon status="gray" />
          <span class="text-tonal-black">Not Eligible</span>
        </div>
        <div class="d-flex align-center">
          <StatusIcon status="notclaimed" />
          <span class="text-tonal-black">Not Claimed</span>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';
  import { useGlossaryStore } from '../../store';
  import type { Report, GradeInfo } from '../../store';
  import StatusIcon from '../common/StatusIcon.vue';
  import { formatGradeName } from '../../lib/grades';
  import type { RouteLocationRaw } from 'vue-router';

  const route = useRoute();
  const glossaryStore = useGlossaryStore();

  const props = withDefaults(
    defineProps<{
      title?: string;
      tableCaption?: string;
      report: Report | null;
    }>(),
    {
      title: 'Ratings Snapshot',
      tableCaption:
        'Performance status for each gateway across available grades',
    },
  );

  const mockGradeHeaders = ['K', '1', '2', '3', '4', '5', '6'];
  const mockGatewayRowLabels = ['Gateway 1', 'Gateway 2', 'Gateway 3'];
  const mockStatusMatrix = [
    ['red', 'yellow', 'green', 'green', 'yellow', 'yellow', 'green'],
    ['red', 'yellow', 'green', 'green', 'yellow', 'yellow', 'green'],
    ['gray', 'yellow', 'green', 'orange', 'orange', 'green', 'orange'],
  ];

  const currentGradeShortcode = computed(() => {
    const gradeParam = route.params.grade;
    if (typeof gradeParam !== 'string') return null;
    const gradeSlug = gradeParam.toLowerCase();
    const gradeMap: Record<string, string> = {
      kindergarten: 'K',
      first: '1',
      second: '2',
      third: '3',
      fourth: '4',
      fifth: '5',
      sixth: '6',
      seventh: '7',
      eighth: '8',
      ninth: '9',
      tenth: '10',
      eleventh: '11',
      twelfth: '12',
    };
    const gradeWord = gradeSlug.split('-')[0];
    if (gradeMap[gradeWord]) return gradeMap[gradeWord];
    if (gradeSlug === 'k') return 'K';
    if (/^\d+$/.test(gradeSlug)) return gradeSlug.toUpperCase();
    return null;
  });

  const snapshotGrades = computed<GradeInfo[]>(() => {
    if (!props.report?.gateway_snapshots) return [];

    const allGrades = new Map<string, GradeInfo>();
    props.report.gateway_snapshots.forEach((snapshot) => {
      snapshot.scores.forEach((score) => {
        if (score.grade && !allGrades.has(score.grade.slug)) {
          allGrades.set(score.grade.slug, score.grade);
        }
      });
    });
    return Array.from(allGrades.values()).sort((a, b) => a.order - b.order);
  });

  // Update snapshotGatewayLabels to show individual grades
  const snapshotGatewayLabels = computed<string[]>(() => {
    if (!props.report?.gateway_snapshots) return [];

    // Group gateways by their number
    const gatewayGroups = new Map<string, any[]>();
    props.report.gateway_snapshots.forEach((gs) => {
      const match = gs.label.match(/Gateway\s+(\d+)/i);
      if (match) {
        const number = match[1];
        if (!gatewayGroups.has(number)) {
          gatewayGroups.set(number, []);
        }
        gatewayGroups.get(number)?.push(gs);
      }
    });

    // Check if we have multiple Gateway 1s with grade-specific labels
    const gateway1Group = gatewayGroups.get('1') || [];
    const hasMultipleGateway1s = gateway1Group.some((gs) =>
      gs.label.includes('('),
    );

    // Create the final labels array
    const labels: string[] = [];
    Array.from(gatewayGroups.entries())
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .forEach(([number, gateways]) => {
        if (number === '1') {
          // Always add the main Gateway 1 row
          labels.push('Gateway 1');

          // If we have grade-specific Gateway 1s, add them after the main row
          if (hasMultipleGateway1s) {
            // Sort grade-specific Gateway 1s by grade order
            const gradeSpecificGateway1s = gateways
              .filter((gs) => gs.label.includes('('))
              .sort((a, b) => {
                const gradeA = a.grades?.[0]?.order || 0;
                const gradeB = b.grades?.[0]?.order || 0;
                return gradeA - gradeB;
              });

            // Add each grade-specific Gateway 1 label
            gradeSpecificGateway1s.forEach((gs) => {
              const match = gs.label.match(/\((.*?)\)/);
              if (match) {
                labels.push(match[1]);
              }
            });
          }
        } else {
          // For Gateway 2 and up, just add the gateway number
          labels.push(`Gateway ${number}`);
        }
      });

    return labels;
  });

  const gatewayStatusMatrix = computed(() => {
    if (
      !props.report?.gateway_snapshots ||
      snapshotGrades.value.length === 0 ||
      snapshotGatewayLabels.value.length === 0
    ) {
      return [];
    }

    const snapshots = props.report.gateway_snapshots;

    // Create the status matrix
    return snapshotGatewayLabels.value.map((label) => {
      // Check if we have multiple Gateway 1s
      const hasMultipleGateway1s = snapshots.some(
        (gs) => gs.label.startsWith('Gateway 1') && gs.label.includes('('),
      );

      // For Gateway 1 row when there are multiple Gateway 1s, show empty cells
      if (label === 'Gateway 1' && hasMultipleGateway1s) {
        return snapshotGrades.value.map(() => undefined);
      }

      // For all other cases (including single Gateway 1), find the matching snapshot
      const gatewaySnapshot = snapshots.find(
        (gs) => gs.label === label || gs.label.includes(`(${label})`),
      );

      if (!gatewaySnapshot) return snapshotGrades.value.map(() => 'gray');

      return snapshotGrades.value.map((gradeInfo) => {
        const scoreData = gatewaySnapshot.scores.find(
          (s) => s.grade.slug === gradeInfo.slug,
        );
        if (!scoreData || !scoreData.review_status) return 'gray';

        switch (scoreData.review_status.toLowerCase()) {
          case 'meets':
            return 'green';
          case 'partially-meets':
            return 'yellow';
          case 'does-not-meet':
            return 'red';
          case 'not-applicable':
          case 'not-eligible':
          case 'not-claimed':
          default:
            return 'gray';
        }
      });
    });
  });

  const useDynamicData = computed(() => {
    return (
      props.report &&
      props.report.gateway_snapshots &&
      props.report.gateway_snapshots.length > 0 &&
      snapshotGrades.value.length > 0 &&
      snapshotGatewayLabels.value.length > 0
    );
  });

  const numberOfGatewayRows = computed(() => {
    return useDynamicData.value
      ? snapshotGatewayLabels.value.length
      : mockGatewayRowLabels.length;
  });

  const activeIndicatorHeight = computed(() => {
    const rowHeight = 66;
    const paddingTopBottom = 30;
    return `${numberOfGatewayRows.value * rowHeight + paddingTopBottom}px`;
  });

  function openGlossary() {
    glossaryStore.openGlossary('series performance snapshot');
  }

  // Determine if we should create a link for this gateway label
  const canCreateGatewayLink = (gatewayLabel: string) => {
    // For Gateway 1, check if we have multiple Gateway 1s
    if (gatewayLabel === 'Gateway 1') {
      const hasMultipleGateway1s = props.report?.gateway_snapshots?.some(
        (gs) => gs.label.startsWith('Gateway 1') && gs.label.includes('('),
      );
      // Only create link if there's a single Gateway 1
      return !hasMultipleGateway1s;
    }

    // For all other cases (including grade-specific Gateway 1s), create a link
    return true;
  };

  // Update the route handling to be more explicit
  interface GatewayRoute {
    name: 'report-gateway';
    params: {
      id: string;
      grade: string;
      gateway: string;
    };
  }

  // Update getGatewayRoute to handle grade extraction correctly
  const getGatewayRoute = (gatewayLabel: string): RouteLocationRaw => {
    const reportSlug = route.params.id as string;

    // If this is a grade-specific label (not starting with "Gateway")
    if (!gatewayLabel.includes('Gateway')) {
      // Find the gateway snapshot that contains this grade
      const matchingSnapshot = props.report?.gateway_snapshots?.find((gs) => {
        const gradeMatch = gs.label.match(/\((.*?)\)/);
        return gradeMatch && gradeMatch[1] === gatewayLabel;
      });

      if (matchingSnapshot) {
        // Get the grade band slug from scores (e.g., "kindergarten-to-second")
        const gradeBandSlug = matchingSnapshot.scores[0]?.grade?.slug;

        // Get the individual grade slug from grades array (e.g., "first-grade")
        const individualGradeSlug = matchingSnapshot.grades?.[0]?.slug;

        console.log('Gateway route generation:', {
          label: gatewayLabel,
          gradeBandSlug,
          individualGradeSlug,
          finalUrl: `${gradeBandSlug}/gateway-one__${individualGradeSlug}`,
        });

        if (gradeBandSlug && individualGradeSlug) {
          return {
            name: 'report-gateway',
            params: {
              id: reportSlug,
              grade: gradeBandSlug, // Use grade band for URL path
              gateway: `gateway-one__${individualGradeSlug}`, // Use individual grade for gateway
            },
          };
        }
      }
    }

    // For gateway rows
    const gatewayNumber = gatewayLabel.match(/Gateway\s+(\d+)/i)?.[1];
    if (!gatewayNumber) {
      return {
        name: 'report-overview',
        params: { id: reportSlug },
      };
    }

    // Special handling for Gateway 1
    if (gatewayNumber === '1') {
      // Check if we have multiple Gateway 1s
      const gateway1Snapshots =
        props.report?.gateway_snapshots?.filter((gs) =>
          gs.label.startsWith('Gateway 1'),
        ) || [];

      const hasMultipleGateway1s = gateway1Snapshots.some((gs) =>
        gs.label.includes('('),
      );

      // Use current route's grade or first available grade
      let gradeSlug = route.params.grade as string;
      if (!gradeSlug && snapshotGrades.value.length > 0) {
        gradeSlug = snapshotGrades.value[0].slug;
      }

      if (!gradeSlug) {
        return {
          name: 'report-overview',
          params: { id: reportSlug },
        };
      }

      // For single Gateway 1, use simple gateway-one format
      if (!hasMultipleGateway1s) {
        return {
          name: 'report-gateway',
          params: {
            id: reportSlug,
            grade: gradeSlug,
            gateway: 'gateway-one',
          },
        };
      }

      // For multiple Gateway 1s, we need to find the specific one for this grade
      const gateway1Snapshot = gateway1Snapshots.find((gs) =>
        gs.scores?.some((score) => score.grade.slug === gradeSlug),
      );

      if (gateway1Snapshot) {
        return {
          name: 'report-gateway',
          params: {
            id: reportSlug,
            grade: gradeSlug,
            gateway: `gateway-one__${gradeSlug}`,
          },
        };
      }

      // If we can't find a matching snapshot, go to overview
      return {
        name: 'report-overview',
        params: { id: reportSlug },
      };
    }

    // Use current route's grade or first available grade
    let gradeSlug = route.params.grade as string;
    if (!gradeSlug && snapshotGrades.value.length > 0) {
      gradeSlug = snapshotGrades.value[0].slug;
    }

    if (!gradeSlug) {
      return {
        name: 'report-overview',
        params: { id: reportSlug },
      };
    }

    const gatewayWords = ['one', 'two', 'three', 'four', 'five'];
    const gatewayWord =
      gatewayWords[parseInt(gatewayNumber) - 1] || gatewayNumber;

    return {
      name: 'report-gateway',
      params: {
        id: reportSlug,
        grade: gradeSlug,
        gateway: `gateway-${gatewayWord}`,
      },
    };
  };

  // Add a computed property to safely get routes
  const getRouteIfValid = (gatewayLabel: string): RouteLocationRaw => {
    const routeResult = getGatewayRoute(gatewayLabel);
    console.log('Route generation for:', {
      label: gatewayLabel,
      route: routeResult,
    });

    if (!routeResult) {
      // Return a safe fallback route
      return {
        name: 'report-overview',
        params: {
          id: route.params.id as string,
        },
      };
    }
    return routeResult;
  };
</script>

<style lang="scss" scoped>
  .performance-snapshot {
    border-radius: 8px;
    padding: 24px;
    box-shadow: none;
  }

  .performance-table {
    border: none;
    border-radius: 3px;
    background: white;
    :deep(.v-table__wrapper) {
      overflow: visible !important;
    }

    :deep(th) {
      position: relative;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.87);
      background: white;
      white-space: nowrap;
      &:first-child {
        min-width: 180px;
        width: 180px;
      }
      .th-wrapper {
        position: relative;
        .cell-content {
          position: relative;
          z-index: 1;
        }
        .active-grade-indicator {
          z-index: 1;
          position: absolute;
          top: -15px;
          left: 0;
          width: 100%;
          background: rgba(var(--v-theme-primary), 0.05);
          border-radius: 3px;
          border: 2px solid rgba(var(--v-theme-primary), 0.5);
        }
      }
    }

    :deep(td) {
      background: white;
      white-space: nowrap;
      &:first-child {
        min-width: 180px;
        width: 180px;
      }
      .v-icon {
        margin: 0 !important;
      }

      // Add indentation for grade-specific Gateway 1s
      &.grade-specific {
        padding-left: 40px !important;
      }
    }
  }

  .legend {
    color: rgba(0, 0, 0, 0.6);
  }

  .v-assistive-text {
    position: absolute !important;
    overflow: hidden !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    width: 1px !important;
    height: 1px !important;
    white-space: nowrap !important; // Or 'normal' depending on content
  }

  /* Print styles to fix white overlay issue */
  @media print {
    /* Force color printing for all elements */
    *,
    *::before,
    *::after {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    /* Remove ALL v-card backgrounds globally */
    :deep(.v-card) {
      background: transparent !important;
      background-color: transparent !important;
      box-shadow: none !important;
      border: none !important;
    }

    .performance-snapshot {
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
    }

    .performance-table {
      background: white !important;
      border: 1px solid #333 !important;

      :deep(th) {
        background: white !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      :deep(td) {
        background: white !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }

    /* Ensure all chart elements and status icons are visible */
    :deep(.v-icon),
    :deep(svg),
    :deep(circle),
    :deep(rect),
    :deep(.status-icon),
    :deep([class*='status-']) {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      opacity: 1 !important;
      visibility: visible !important;
    }
  }
</style>

<style lang="scss">
  // Gateway link styles for both overview and gateway views
  .v-application
    .performance-snapshot
    .performance-table
    td
    a:not(.v-btn):not(.v-tab),
  .v-application
    #summary-alignment-usability
    .performance-table
    td
    a:not(.v-btn):not(.v-tab) {
    color: rgb(var(--v-theme-secondary)) !important;
    text-decoration: none !important;
    font-weight: 600 !important;
    font-size: inherit !important;
    font-family: inherit !important;
    transition: opacity 0.2s ease-in-out !important;

    &:hover {
      opacity: 0.8;
      color: rgb(var(--v-theme-secondary)) !important;
    }
  }

  .performance-table tr {
    background: white;

    &:last-child td {
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }
  }

  .legend {
    color: rgba(0, 0, 0, 0.6);
  }

  .v-assistive-text {
    position: absolute !important;
    overflow: hidden !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    width: 1px !important;
    height: 1px !important;
    white-space: nowrap !important;
  }
</style>
